import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@crayon/design-system-react";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  ProgramStatus,
  ProgramTargetMigrationModel,
  ProgramType,
  Source,
  Target,
} from "api/client.generated";
import { ValidationException } from "api/client.generated.extensions";
import FormCheckbox from "components/primitives/FormCheckbox";
import FormErrorMessage from "components/primitives/FormErrorMessage";
import TableBodyCell from "components/primitives/TableBodyCell";
import TableBodySkeleton from "components/primitives/TableBodySkeleton";
import TableHeaderCell from "components/primitives/TableHeaderCell";
import useSourcePartnerConfiguration from "hooks/api/PartnerMigration/useSourcePartnerConfiguration";
import useApi from "hooks/api/useApi";
import usePartnerConfiguration from "hooks/api/usePartnerConfiguration";
import { ChecklistIcon } from "images/MuiIcons";
import { useEffect, useState } from "react";
import { useFieldArray, useForm, useWatch } from "react-hook-form";
import NotificationMessage from "types/notification-message";
import getProgramTypeDisplayName from "utils/program-type";
import * as yup from "yup";
import { useAuthorizedTargetsContext } from "../AuthorizedTargetsContext";
import MigratePartnerStepProps from "../MigratePartnerStepProps";
import MigrationFormActionButtons from "../MigrationFormActionButtons";
import WarningIconWithTooltip, { TooltipMessage } from "../WarningIconWithTooltip";

const ProgramsStep = ({ onStepComplete }: MigratePartnerStepProps) => {
  const { sourcePartnerConfiguration, isSourcePartnerConfigurationFetching } =
    useSourcePartnerConfiguration();
  const { authorizedTargets } = useAuthorizedTargetsContext();

  const { invalidatePartnerConfig } = usePartnerConfiguration(false);

  const { partnersMigrationClient } = useApi();
  const [formErrorMsg, setFormErrorMsg] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const formSchema = yup.object().shape({
    items: yup.array().of(
      yup.object().shape({
        checked: yup.boolean(),
        target: yup.string().oneOf(Object.values(Target)).required(),
        programType: yup.string().oneOf(Object.values(ProgramType)).required(),
        isTargetAuthorized: yup.boolean(),
      }),
    ),
  });

  type SchemaType = yup.InferType<typeof formSchema>;

  const { control, handleSubmit } = useForm<SchemaType>({
    resolver: yupResolver(formSchema),
    defaultValues: {
      items: [],
    },
  });

  const { fields, append, remove } = useFieldArray({ control, name: "items", keyName: "fieldId" });
  const fieldsWatch = useWatch({ control, name: "items" });

  useEffect(() => {
    if (isSourcePartnerConfigurationFetching || !sourcePartnerConfiguration) return;

    // if rerender, clean up fields to avoid duplication
    if (fields.length > 0) remove([...Array(fields.length).keys()]);

    sourcePartnerConfiguration.configuredPrograms
      .filter((x) => x.source === Source.Prism)
      .forEach((x) =>
        x.programs.forEach((p) => {
          if (p.status === ProgramStatus.Syncing) {
            append({
              checked: false,
              target: x.target,
              programType: p.type,
              isTargetAuthorized: authorizedTargets.some((t) => t === x.target),
            });
          }
        }),
      );
  }, [
    authorizedTargets,
    sourcePartnerConfiguration,
    isSourcePartnerConfigurationFetching,
    append,
    remove,
    fields.length,
  ]);

  const onSave = async (formData: SchemaType): Promise<void> => {
    try {
      setIsSaving(true);

      const apiModel: ProgramTargetMigrationModel[] =
        formData.items
          ?.filter((x) => x.checked)
          .map((x) => ({
            target: x.target,
            programType: x.programType,
          })) ?? [];

      if (apiModel.length === 0) return;

      await partnersMigrationClient.migrateProgramConfigurations(apiModel);

      invalidatePartnerConfig();
      await onStepComplete();
    } catch (e: unknown) {
      const validationError = ValidationException.parse(e);
      if (validationError) {
        setFormErrorMsg(validationError.getFirstErrorMessage());
      } else {
        setFormErrorMsg(NotificationMessage.UNKNOWN_ERROR);
      }
    } finally {
      setIsSaving(false);
    }
  };

  const DataTableHead = (
    <TableHead>
      <TableRow>
        <TableCell size="small" sx={{ width: "45px" }}>
          <Box display="flex">
            <ChecklistIcon />
          </Box>
        </TableCell>
        <TableHeaderCell columnName="PSA Program" />
      </TableRow>
    </TableHead>
  );

  const DataTableBody = (
    <TableBody>
      {fields.map((x, i) => (
        <TableRow key={x.fieldId}>
          <TableCell>
            <FormCheckbox
              control={control}
              bindSchemaFieldName={`items.${i}.checked`}
              label=""
              disabled={isSaving || !x.isTargetAuthorized}
            />
          </TableCell>
          <TableBodyCell
            label={`${getProgramTypeDisplayName(x.programType)} for ${x.target}`}
            {...(!x.isTargetAuthorized && {
              icon: <WarningIconWithTooltip message={TooltipMessage.PsaNotAuthorized} />,
            })}
          />
        </TableRow>
      ))}
    </TableBody>
  );

  return (
    <form onSubmit={handleSubmit(onSave)}>
      <Box display="flex" flexDirection="column" gap={1} minWidth="500px" width="40%">
        <TableContainer component={Paper}>
          <Table testId="migrate-programs-table">
            {DataTableHead}
            {isSourcePartnerConfigurationFetching ? (
              <TableBodySkeleton colsCount={2} rowsCount={3} />
            ) : (
              DataTableBody
            )}
          </Table>
        </TableContainer>

        <FormErrorMessage message={formErrorMsg} />

        <MigrationFormActionButtons
          isSaveDisable={!fieldsWatch?.some((x) => x.checked)}
          isSaveLoading={isSaving}
        />
      </Box>
    </form>
  );
};

export default ProgramsStep;
